import React, { useCallback, useEffect, useState } from 'react';
import { authService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { confirmacaoDeEncerrarSessao, fraseDaListaDeSegurancaQueNaoLeu } from '../utils/configuracoes';

export interface SessaoAtiva {
  id: string;
  label: string;
  ipAddress: string | null;
  iniciadaEm: string;
  ultimaAtividade: string;
  expiraEm: string;
  atual: boolean;
}

/**
 * Onde a conta está logada, e como derrubar.
 *
 * Uma "sessão" é a família de refresh tokens, não o token: renovar não cria
 * sessão nova. É a peça que faltava para responder "tem alguém logado na minha
 * conta que não sou eu?" — antes só dava para revogar passkey e dispositivo
 * confiável, nunca a sessão em si.
 */
export default function ActiveSessionsSection({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [sessoes, setSessoes] = useState<SessaoAtiva[]>([]);
  const [loading, setLoading] = useState(false);
  const [encerrando, setEncerrando] = useState<string | 'outras' | null>(null);
  // D3 (11/10/2026): a lista que não carregava virava "Nenhuma sessao ativa encontrada". Quem abre para conferir se
  // há alguém logado com a sua conta lia "ninguém", e não agia.
  const [erro, setErro] = useState<string | null>(null);
  const confirmar = useConfirm();
  const avisar = useAviso();

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const dados = await authService.listSessions();
      setSessoes(dados);
      setErro(null);
      onCountChange?.(dados.length);
    } catch (e) {
      setSessoes([]);
      setErro(mensagemDoErroDeCarga(e));
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // D3: encerrar UMA era sem pergunta e sem catch: a falha calava e o operador achava que tinha encerrado.
  const encerrar = async (sessao: SessaoAtiva) => {
    if (!(await confirmar(confirmacaoDeEncerrarSessao(sessao, fmtDateTime(sessao.ultimaAtividade))))) return;
    setEncerrando(sessao.id);
    try {
      await authService.revokeSession(sessao.id);
      await carregar();
    } catch (e: any) {
      avisar(`Não encerrei a sessão: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setEncerrando(null);
    }
  };

  const encerrarOutras = async () => {
    if (!(await confirmar({
      title: 'Encerrar todas as outras sessões?',
      message: 'Você continua conectado neste navegador.',
      consequence: 'Os outros navegadores precisam fazer login de novo.',
      confirmLabel: 'Encerrar as outras',
      danger: true,
    }))) return;
    setEncerrando('outras');
    try {
      await authService.revokeOtherSessions();
      await carregar();
    } catch (e: any) {
      avisar(`Não encerrei as outras sessões: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
    } finally {
      setEncerrando(null);
    }
  };

  const outras = sessoes.filter((s) => !s.atual).length;

  return (
    <>
      <p className='text-sm text-slate-400'>
        Navegadores onde sua conta esta conectada. Encerrar uma sessao obriga aquele navegador a fazer login de novo.
      </p>

      {loading && <p className='text-sm text-slate-500'>Carregando...</p>}

      {!loading && erro && (
        <div role='alert' className={`text-sm ${TONE_TEXT.danger} flex items-center gap-3`}>
          <span>{fraseDaListaDeSegurancaQueNaoLeu('as sessões desta conta', erro)}</span>
          <button onClick={carregar} className='px-3 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-xs'>Tentar de novo</button>
        </div>
      )}

      {!loading && !erro && sessoes.length === 0 && (
        <p className='text-sm text-slate-500'>Nenhuma sessao ativa encontrada.</p>
      )}

      {!loading && sessoes.length > 0 && (
        <div className='space-y-2'>
          {sessoes.map((s) => (
            <div
              key={s.id}
              className={`flex items-center justify-between rounded-lg px-4 py-3 ${
                s.atual ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50'
              }`}
            >
              <div className='flex items-center gap-3 min-w-0'>
                <svg className='w-5 h-5 text-slate-400 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                </svg>
                <div className='min-w-0'>
                  <p className='text-sm text-white font-medium flex items-center gap-2'>
                    {s.label}
                    {s.atual && (
                      <span className='px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400'>
                        este navegador
                      </span>
                    )}
                  </p>
                  <p className='text-xs text-slate-500 truncate'>
                    {s.ipAddress && `${s.ipAddress} · `}
                    Ultima atividade: {fmtDateTime(s.ultimaAtividade)}
                  </p>
                </div>
              </div>

              {/* A sessão atual não tem botão: para sair dela existe o logout. */}
              {!s.atual && (
                <button
                  onClick={() => encerrar(s)}
                  disabled={encerrando !== null}
                  className='p-1.5 text-slate-500 hover:text-red-400 rounded disabled:opacity-50 shrink-0'
                  title='Encerrar esta sessao'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' />
                  </svg>
                </button>
              )}
            </div>
          ))}

          {outras > 0 && (
            <button
              onClick={encerrarOutras}
              disabled={encerrando !== null}
              className='text-xs text-slate-400 hover:text-red-400 disabled:opacity-50'
            >
              {encerrando === 'outras'
                ? 'Encerrando...'
                : `Encerrar as outras ${outras} sessao${outras > 1 ? 'es' : ''}`}
            </button>
          )}
        </div>
      )}
    </>
  );
}
